/* globals __CLIENT__, __DEVELOPMENT__, analytics, ga */

import cookie from 'react-cookie';
import utm from '../../utils/utm';

import authService, { REFERRER_COOKIE_NAME } from './auth';
import { postJSON } from './helpers';

/**
 * Following frequent referrer loss/reset/confusion, we store the referrer in a cookie.
 * This is used in subsequent tracking and page events, available to new tabs and post-login.
 */

// Google Analytics needs to be set explicity.
const storedReferrer = cookie.load(REFERRER_COOKIE_NAME);
if (storedReferrer) {
  ga('set', 'referrer', storedReferrer);
}

// Google Analytics needs e-commerce tracking specifically enabled
if (__CLIENT__) {
  ga('require', 'ecommerce');
}

/**
 * To ensure that campaign information is persistent, store the object at load for 30mins,
 * this is then passed onto track and page calls to override Segment defaults.
 */
export const CAMPAIGN_COOKIE_NAME = 'campaign';

if (__CLIENT__ && !cookie.load(CAMPAIGN_COOKIE_NAME)) {
  const params = utm(window.location.search);
  if (Object.keys(params).length) {
    cookie.save(CAMPAIGN_COOKIE_NAME, params, {
      domain: window.location.hostname,
      expires: new Date(+new Date() + (30 * 60 * 1000)), // 30mins from now
      path: '/', // Required to be used site-wide
      secure: !__DEVELOPMENT__,
    });
  }
}

// Configuration for Tracking API
export const TRACKING_ROOT = '/track/';

const trackingService = {};

// See notes above
const trackingReferrer = cookie.load(REFERRER_COOKIE_NAME);

// Load google analytics client id https://segment.com/docs/destinations/google-analytics/#combining-server-side-and-client-side-events
const googleAnalyticsClientId = cookie.load('_ga');

if (trackingReferrer) {
  trackingService.referrer = trackingReferrer;
}

// Resolve data event type/slug to Tracking API
trackingService.getURLforDataEvent = (eventType) => {
  if (!eventType) {
    throw new ReferenceError(`Tracking event data "${eventType}" is not supported`);
  }
  return `${TRACKING_ROOT}${eventType}`;
};

// Capture browser/page properties of current window
trackingService.getBrowserContext = () =>
  __CLIENT__ && {
    campaign: cookie.load(CAMPAIGN_COOKIE_NAME),
    page: {
      path: window.location.pathname,
      referrer: trackingService.referrer || window.document.referrer,
      search: window.location.search,
      title: window.document.title,
      url: window.location.href,
    },
    userAgent: window.navigator.userAgent,
    ...(googleAnalyticsClientId && {
      'Google Analytics': {
        clientId: googleAnalyticsClientId.replace('GA1.2.', ''),
      } }),
  };

/**
 * Tracking Service methods
 *
 * These functions wrap other services, e.g. our tracking service, or Segment's JS library.
 * The aim here is to provide a simple interface for tracking calls which don't required
 * knowledge the third-parties, or which calls are required to be performed client- or server-side.
 * Additionally, they reuse common properties, e.g. user identification.
 *
 * TODO: This service should probably aligned with the others (API, auth), which are called directly
 * by components, where these are largely called by the tracking utility functions.
 */

/**
 * Track user alias with Segment Analytics alias method.
 */
trackingService.aliasUser = (profile) => {
  if (__CLIENT__ && window.analytics) {
    analytics.ready(() => {
      analytics.alias(profile.primaryIdentityId, analytics.user().anonymousId());
    });
  }
};

/**
 * Track user with Segment Analytics identify method.
 */
trackingService.identifyUser = (profile) => {
  if (__CLIENT__ && window.analytics) {
    analytics.ready(() => {
      analytics.identify(profile.primaryIdentityId);
    });
  }
};

/**
 * Track event and properties with tracking service.
 */
trackingService.trackDataEvent = (eventType, data) => {
  if (__CLIENT__ && window.analytics) {
    analytics.ready(() => {
      const loggedIn = authService.loggedIn();
      const userId = authService.getUserCookie();
      postJSON(
        trackingService.getURLforDataEvent(eventType),
        {},
        {
          data,
          browserContext: trackingService.getBrowserContext(),
          ...(loggedIn ? { userId } : { anonymousId: analytics.user().anonymousId() }),
        },
      );
    });
  }
};

/**
 * Track simple event and properties with Segment Analytics track method.
 */
trackingService.trackEvent = (event, properties) => {
  if (__CLIENT__ && window.analytics) {
    analytics.ready(() => {
      analytics.track(event, properties, {
        context: { campaign: cookie.load(CAMPAIGN_COOKIE_NAME) },
      });
    });
  }
};

/**
 * Track page view and properties with Segment Analytics page method.
 */
trackingService.trackPage = (name, delay = 500) => {
  if (__CLIENT__ && window.analytics) {
    analytics.ready(() => {
      setTimeout(() => {
        analytics.page(
          name,
          { referrer: trackingService.referrer || window.document.referrer },
          { context: { campaign: cookie.load(CAMPAIGN_COOKIE_NAME) } },
        );
      }, delay);
    });
    setTimeout(() => {
      ga('set', 'page', location.pathname);
      ga('send', 'pageview');
    }, delay);
  }
};

export default trackingService;



// WEBPACK FOOTER //
// ./app/src/state/services/tracking.js